import type { MutableRefObject, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { fractionAt } from './geometry';
import './seatmap.css';

/**
 * The picture a seat map is drawn on, and nothing else.
 *
 * Shared by the editor and the booking sheet so the two are laid out against
 * the same box. The aspect ratio comes from the room's stored dimensions and
 * is set *before* the image loads — without it the markers spend one frame
 * positioned against a zero-height element and visibly jump into place when
 * the photo arrives.
 */

export interface CanvasRoom {
  name: string;
  imageUrl: string | null;
  imageW: number | null;
  imageH: number | null;
}

export function SeatMapCanvas({
  room,
  canvasRef,
  onPlace,
  className = '',
  children,
}: {
  room: CanvasRoom;
  canvasRef?: MutableRefObject<HTMLDivElement | null>;
  /**
   * A tap on the picture itself, in fractions. Only fires for the editor —
   * the sheet passes nothing, and the canvas is inert.
   */
  onPlace?: (point: { x: number; y: number }) => void;
  className?: string;
  children?: ReactNode;
}) {
  const w = room.imageW ?? 4;
  const h = room.imageH ?? 3;

  /**
   * Two constraints, both exact.
   *
   * The aspect ratio comes from the room's stored dimensions and is set
   * *before* the image loads — without it the markers spend one frame
   * positioned against a zero-height element and visibly jump into place when
   * the photo arrives.
   *
   * The height cap is the surface's (`--seatmap-max-h`), and it is applied as
   * a *width* limit derived from the ratio rather than as `max-height`. A
   * max-height would leave the box the wrong shape and the image cropped, and
   * every marker on it would then be positioned against a picture that is no
   * longer the one the seats were placed on. A portrait photo of a living room
   * is the ordinary case, so this is not a corner.
   */
  const style = {
    aspectRatio: `${w} / ${h}`,
    maxWidth: `calc(var(--seatmap-max-h, 200vh) * ${w} / ${h})`,
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!onPlace) return;
    // A tap that started on a marker is that marker's business. Without this,
    // grabbing a seat to drag it would also drop a new one underneath.
    if (event.target !== event.currentTarget && (event.target as HTMLElement).tagName !== 'IMG') {
      return;
    }
    onPlace(fractionAt(event, event.currentTarget));
  };

  return (
    <div
      ref={canvasRef}
      className={`seatmap ${className}`.trim()}
      style={style}
      onPointerDown={handlePointerDown}
    >
      {room.imageUrl ? (
        <img
          className="seatmap__image"
          src={room.imageUrl}
          alt={`Raspored mesta — ${room.name}`}
          draggable={false}
        />
      ) : (
        <p className="seatmap__blank">
          Nema slike sobe.
          <span>Bez nje se mesta nemaju na šta postaviti.</span>
        </p>
      )}
      {children}
    </div>
  );
}
